import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, FileText, AlertTriangle } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
function Shell({ children }) { return <div className="min-h-screen bg-slate-100 py-6 px-2 sm:px-4"><div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-sm p-4 sm:p-6">{children}</div></div>; }

export default function WarningPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [disputed, setDisputed] = useState(false);
  const [response, setResponse] = useState("");
  const [witness, setWitness] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => hrApi.get(`/public/warning/${token}`).then((r) => { setD(r.data); setName((n) => n || r.data.employee_name || ""); }).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  useEffect(() => { load(); }, [token]);
  const submit = async () => {
    if (!name.trim()) return toast.error("Type your first and last name");
    if (!sig) return toast.error("Please draw your signature");
    if (disputed && !response.trim()) return toast.error("Please write your response");
    setBusy(true);
    try { await hrApi.post(`/public/warning/${token}/sign`, { typed_name: name, signature_data: sig, disputed, response, witness }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };
  if (err) return <Shell><p data-testid="warning-error" className="text-center text-slate-600">{err}</p></Shell>;
  if (!d) return <Shell><p className="text-center text-slate-400">Loading…</p></Shell>;
  return (
    <Shell>
      <div className="space-y-4" data-testid="warning-page">
        <div className="flex items-center gap-2 text-rose-600 text-xs font-bold uppercase tracking-wider"><AlertTriangle size={16} /> Warning advice · G &amp; L One · Confidential</div>
        <h1 className="font-heading font-extrabold text-2xl text-slate-800">{d.level} warning</h1>
        <div className="rounded-2xl bg-slate-50 p-4 text-sm space-y-1.5">
          <div><b>Date:</b> {d.date}{d.time ? ` @ ${d.time}` : ""}</div>{d.site && <div><b>Site:</b> {d.site}</div>}<div><b>Reason:</b> {d.reason}</div>
          {d.detail && <div><b>Warning:</b> {d.detail}</div>}{d.corrective && <div><b>Corrective actions:</b> {d.corrective}</div>}
        </div>
        <a href={`${BASE}/api/hr/public/warning/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="warning-open-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600"><FileText size={15} /> Open the Warning Advice (PDF)</a>
        {d.done ? (
          <div className="text-center py-6 space-y-2" data-testid="warning-done"><ShieldCheck className="mx-auto text-teal-600" size={32} /><p className="text-slate-600">Thank you. Your {d.status === "disputed" ? "response has" : "acknowledgement has"} been signed and filed with G &amp; L One.</p></div>
        ) : (
          <div className="space-y-3 rounded-2xl border border-indigo-100 bg-indigo-50/30 p-4">
            <div className="flex gap-2">
              <button data-testid="warning-ack" onClick={() => setDisputed(false)} className={`flex-1 min-h-11 px-2 py-2 rounded-xl text-sm font-semibold border-2 ${!disputed ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 bg-white"}`}>I acknowledge receipt</button>
              <button data-testid="warning-dispute" onClick={() => setDisputed(true)} className={`flex-1 min-h-11 px-2 py-2 rounded-xl text-sm font-semibold border-2 ${disputed ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-200 bg-white"}`}>I disagree</button>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Employee response {disputed ? "*" : "(optional)"}</label><Textarea data-testid="warning-response" value={response} onChange={(e) => setResponse(e.target.value)} className="mt-1 bg-white min-h-[70px]" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Witness name (optional)</label><Input data-testid="warning-witness" value={witness} onChange={(e) => setWitness(e.target.value)} className="mt-1 bg-white" /></div>
            <NameInputs value={name} onChange={setName} testid="warning-name" className="mt-1 bg-white h-11" />
            <div><label className="text-xs font-semibold text-slate-600">Your signature *</label><SignaturePad onChange={setSig} /></div>
            <p className="text-[11px] text-slate-500">Signing confirms you have received this warning. It does not mean you agree with it.</p>
            <Button data-testid="warning-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-xl bg-indigo-600">{busy ? "Signing…" : "Sign & submit"}</Button>
          </div>
        )}
      </div>
    </Shell>
  );
}
